import { Injectable } from '@angular/core';

import { Offer } from '../model/offer';
import { OfferCategory, OFFER_CATEGORIES, OFFER_CATEGORY_ALIASES, OFFER_CATEGORY_ORDER } from '../model/offer-category.model';
import { UpgradeAvailabilityDTO } from '../model/upgradeAvailabilityDTO';

@Injectable({
  providedIn: 'root',
})
export class OfferCategoryService {
  private readonly unknownCategoryReports = new Set<string>();

  isCategory(value: unknown): value is OfferCategory {
    return typeof value === 'string' && (OFFER_CATEGORIES as readonly string[]).includes(value);
  }

  parseApiCategory(
    value: unknown,
    opts?: { failOnUnknown?: boolean; context?: string },
  ): OfferCategory | null {
    if (typeof value !== 'string') {
      this.reportUnknownCategory(value, opts?.context);
      if (opts?.failOnUnknown) {
        throw new Error(`Invalid ofr_category from API: ${String(value)}`);
      }
      return null;
    }

    const normalized = value.trim().toUpperCase();
    const mapped = OFFER_CATEGORY_ALIASES[normalized];

    if (mapped) {
      return mapped;
    }

    this.reportUnknownCategory(value, opts?.context);
    if (opts?.failOnUnknown) {
      throw new Error(`Unknown ofr_category from API: ${normalized}`);
    }

    return null;
  }

  normalizeCategory(value: unknown, fallback: OfferCategory = 'ROOM'): OfferCategory {
    if (typeof value !== 'string') {
      return fallback;
    }

    const normalized = value.trim().toUpperCase();
    return OFFER_CATEGORY_ALIASES[normalized] ?? fallback;
  }

  getOfferCategory(offer: Offer | null | undefined, fallback: OfferCategory = 'ROOM'): OfferCategory {
    if (!offer) return fallback;

    const fromOfrCategory = this.normalizeCategory(offer.ofr_category, 'ROOM');
    if (fromOfrCategory !== 'ROOM' || (offer.ofr_category ?? '').toUpperCase() === 'ROOM') {
      return fromOfrCategory;
    }

    const legacyPrefix = (offer.upsell_room_category_cd ?? '').substring(0, 3);
    return this.normalizeCategory(legacyPrefix, fallback);
  }

  isRoomCategory(value: unknown): boolean {
    return this.normalizeCategory(value) === 'ROOM';
  }

  isAncillaryCategory(value: unknown): boolean {
    return this.normalizeCategory(value) === 'ANCILLARY';
  }

  isOfferInCategory(offer: Offer | null | undefined, category: OfferCategory): boolean {
    if (!offer) return false;
    return this.getOfferCategory(offer) === category;
  }

  getFirstAvailableCategory(upgrade: UpgradeAvailabilityDTO | null | undefined): OfferCategory | null {
    if (!upgrade) return null;
    if (upgrade.room_available) return 'ROOM';
    if (upgrade.eci_available) return 'ECI';
    if (upgrade.lco_available) return 'LCO';
    if (upgrade.addon_available) return 'ANCILLARY';
    return null;
  }

  isCategoryAvailable(category: OfferCategory, upgrade: UpgradeAvailabilityDTO | null | undefined): boolean {
    if (!upgrade) return false;
    if (category === 'ROOM') return !!upgrade.room_available;
    if (category === 'ECI') return !!upgrade.eci_available;
    if (category === 'LCO') return !!upgrade.lco_available;
    return !!upgrade.addon_available;
  }

  getVisibleTabsForAttention(upgrade: UpgradeAvailabilityDTO | null | undefined): OfferCategory[] {
    if (!upgrade) return ['ROOM'];

    const tabs: OfferCategory[] = ['ROOM'];
    if (upgrade.eci_available) tabs.push('ECI');
    if (upgrade.lco_available) tabs.push('LCO');
    if (upgrade.addon_available) tabs.push('ANCILLARY');
    return tabs;
  }

  getNextCategoryInSequence(
    active: OfferCategory,
    seen: Record<OfferCategory, boolean>,
    upgrade: UpgradeAvailabilityDTO | null | undefined,
  ): OfferCategory | null {
    const ordered: Record<OfferCategory, OfferCategory[]> = {
      ROOM: ['ECI', 'LCO', 'ANCILLARY'],
      ECI: ['LCO', 'ANCILLARY'],
      LCO: ['ANCILLARY'],
      ANCILLARY: [],
    };

    for (const candidate of ordered[active]) {
      if (this.isCategoryAvailable(candidate, upgrade) && !seen[candidate]) {
        return candidate;
      }
    }

    return null;
  }

  getOrderedCategories(): readonly OfferCategory[] {
    return OFFER_CATEGORY_ORDER;
  }

  private reportUnknownCategory(value: unknown, context?: string): void {
    const key = `${String(value)}|${context ?? ''}`;
    if (this.unknownCategoryReports.has(key)) {
      return;
    }

    this.unknownCategoryReports.add(key);
    console.warn('[OfferCategoryService] Unknown ofr_category received from API', {
      ofr_category: value,
      context,
    });
  }
}
